import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../constants/config';
import * as newsApi from '../../api/news';
import AdminLayout from './AdminLayout';
import styles from './AdminNews.module.css';

const TYPE_LABELS = { text: 'Запись', link: 'Ссылка', event: 'Мероприятие' };

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU');
};

const formatDateTimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EMPTY_FORM = {
  type: 'text',
  content: '',
  link_url: '',
  link_title: '',
  link_image: '',
  event_title: '',
  event_date: '',
  event_location: '',
  published: false,
  published_at: '',
  show_as_admin: false,
  pinned: false,
};

const AdminNews = () => {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null); // null = создание, post = редактирование
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [formMessage, setFormMessage] = useState({ text: '', type: '' });

  // Изображение для поста типа link
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const fileInputRef = useRef(null);

  const fetchPosts = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await newsApi.getAllPostsAdmin(token);
      setPosts(data.posts);
    } catch {
      setError('Ошибка загрузки постов');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const openCreate = () => {
    setEditingPost(null);
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    setFormMessage({ text: '', type: '' });
    setShowForm(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setForm({
      type: post.type,
      content: post.content || '',
      link_url: post.link_url || '',
      link_title: post.link_title || '',
      link_image: post.link_image || '',
      event_title: post.event_title || '',
      event_date: formatDateTimeLocal(post.event_date),
      event_location: post.event_location || '',
      published: post.published,
      published_at: formatDateTimeLocal(post.published_at),
      show_as_admin: post.show_as_admin || false,
      pinned: post.pinned || false,
    });
    setImageFile(null);
    // Показать превью если есть загруженное изображение
    if (post.link_image && post.link_image.startsWith('/uploads/')) {
      setImagePreview(`${API_URL}${post.link_image}`);
    } else {
      setImagePreview(null);
    }
    setRemoveImage(false);
    setFormMessage({ text: '', type: '' });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPost(null);
    setImageFile(null);
    setImagePreview(null);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setRemoveImage(false);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setFormMessage({ text: '', type: '' });

    // Используем FormData для поддержки загрузки файлов
    const fd = new FormData();
    fd.append('type', form.type);
    fd.append('content', form.content || '');
    fd.append('link_url', form.link_url || '');
    fd.append('link_title', form.link_title || '');
    fd.append('event_title', form.event_title || '');
    fd.append('event_date', form.event_date || '');
    fd.append('event_location', form.event_location || '');
    fd.append('published', String(form.published));
    fd.append('published_at', form.published_at || '');
    fd.append('show_as_admin', String(form.show_as_admin));
    fd.append('pinned', String(form.pinned));

    // Изображение: файл имеет приоритет над URL
    if (imageFile) {
      fd.append('image', imageFile);
    } else if (!removeImage) {
      // Передаём текстовый URL если нет файла и не удаляем
      fd.append('link_image', form.link_image || '');
    }
    if (removeImage) {
      fd.append('remove_image', 'true');
    }

    try {
      if (editingPost) {
        await newsApi.updatePost(token, editingPost.id, fd);
        setFormMessage({ text: 'Пост обновлён', type: 'success' });
      } else {
        await newsApi.createPost(token, fd);
        setFormMessage({ text: 'Пост создан', type: 'success' });
      }
      fetchPosts();
      setTimeout(closeForm, 1000);
    } catch (err) {
      setFormMessage({ text: err.response?.data?.message || 'Ошибка сохранения', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTogglePin = async (post) => {
    try {
      await newsApi.updatePost(token, post.id, { pinned: !post.pinned });
      fetchPosts();
    } catch {
      alert('Ошибка при изменении закрепления');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить пост? Это действие нельзя отменить.')) return;
    try {
      await newsApi.deletePost(token, id);
      setPosts((prev) => prev.filter((p) => p.id !== id));
    } catch {
      alert('Ошибка при удалении');
    }
  };

  const isScheduled = (post) =>
    post.published && post.published_at && new Date(post.published_at) > new Date();

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>Новости</h1>
          <button className={styles.createBtn} onClick={openCreate}>
            + Создать пост
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {/* Форма создания/редактирования */}
        {showForm && (
          <div className={styles.formWrap}>
            <div className={styles.formHeader}>
              <h2 className={styles.formTitle}>
                {editingPost ? 'Редактировать пост' : 'Новый пост'}
              </h2>
              <button className={styles.closeBtn} onClick={closeForm}>✕</button>
            </div>

            {formMessage.text && (
              <div className={`${styles.formMessage} ${styles[formMessage.type]}`}>
                {formMessage.text}
              </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Тип поста */}
              <div className={styles.field}>
                <label className={styles.label}>Тип поста</label>
                <select name="type" value={form.type} onChange={handleFormChange} className={styles.input}>
                  <option value="text">Запись</option>
                  <option value="link">Ссылка</option>
                  <option value="event">Мероприятие</option>
                </select>
              </div>

              {/* Текст */}
              <div className={styles.field}>
                <label className={styles.label}>Текст</label>
                <textarea
                  name="content"
                  value={form.content}
                  onChange={handleFormChange}
                  className={styles.textarea}
                  rows={4}
                  placeholder="Текст публикации..."
                />
              </div>

              {/* Поля для ссылки */}
              {form.type === 'link' && (
                <>
                  <div className={styles.field}>
                    <label className={styles.label}>URL ссылки</label>
                    <input name="link_url" value={form.link_url} onChange={handleFormChange} className={styles.input} placeholder="https://..." />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>Заголовок ссылки</label>
                    <input name="link_title" value={form.link_title} onChange={handleFormChange} className={styles.input} placeholder="Название страницы" />
                  </div>

                  {/* Загрузка изображения */}
                  <div className={styles.field}>
                    <label className={styles.label}>Изображение превью</label>
                    <div className={styles.imageUpload}>
                      {imagePreview ? (
                        <div className={styles.imagePreviewWrap}>
                          <img src={imagePreview} alt="Preview" className={styles.imagePreview} />
                          <button
                            type="button"
                            className={styles.removeImageBtn}
                            onClick={handleRemoveImage}
                          >
                            ✕ Удалить
                          </button>
                        </div>
                      ) : (
                        <label className={styles.uploadArea} htmlFor="news-image">
                          <span className={styles.uploadIcon}>📷</span>
                          <span className={styles.uploadText}>Загрузить изображение</span>
                          <span className={styles.uploadHint}>JPEG, PNG, WebP · до 10 МБ</span>
                        </label>
                      )}
                      <input
                        id="news-image"
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleFileChange}
                        className={styles.fileInput}
                      />
                    </div>
                    <p className={styles.hint}>
                      Если не загружено — для YouTube/Rutube будет использовано авто-превью
                    </p>
                  </div>

                  {/* URL изображения вручную (если нет загруженного файла) */}
                  {!imageFile && !imagePreview && (
                    <div className={styles.field}>
                      <label className={styles.label}>Или URL изображения</label>
                      <input name="link_image" value={form.link_image} onChange={handleFormChange} className={styles.input} placeholder="https://..." />
                    </div>
                  )}
                </>
              )}

              {/* Поля для мероприятия */}
              {form.type === 'event' && (
                <>
                  <div className={styles.field}>
                    <label className={styles.label}>Название мероприятия</label>
                    <input name="event_title" value={form.event_title} onChange={handleFormChange} className={styles.input} placeholder="Название..." />
                  </div>
                  <div className={styles.formGrid}>
                    <div className={styles.field}>
                      <label className={styles.label}>Дата и время</label>
                      <input type="datetime-local" name="event_date" value={form.event_date} onChange={handleFormChange} className={styles.input} />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label}>Место проведения</label>
                      <input name="event_location" value={form.event_location} onChange={handleFormChange} className={styles.input} placeholder="Адрес или онлайн" />
                    </div>
                  </div>
                </>
              )}

              {/* Публикация */}
              <div className={styles.publishRow}>
                <label className={styles.checkboxLabel}>
                  <input type="checkbox" name="published" checked={form.published} onChange={handleFormChange} />
                  Опубликовать
                </label>
                <label className={styles.checkboxLabel}>
                  <input type="checkbox" name="show_as_admin" checked={form.show_as_admin} onChange={handleFormChange} />
                  От имени администратора
                </label>
                <label className={styles.checkboxLabel}>
                  <input type="checkbox" name="pinned" checked={form.pinned} onChange={handleFormChange} />
                  Закрепить пост
                </label>
              </div>

              {/* Отложенная публикация */}
              <div className={styles.field}>
                <label className={styles.label}>Отложенная публикация (дата и время)</label>
                <input
                  type="datetime-local"
                  name="published_at"
                  value={form.published_at}
                  onChange={handleFormChange}
                  className={styles.input}
                />
                <p className={styles.hint}>
                  Если дата в будущем — пост будет скрыт до этого момента (даже если «Опубликовать» отмечено)
                </p>
              </div>

              <div className={styles.formActions}>
                <button type="submit" className={styles.saveBtn} disabled={isSaving}>
                  {isSaving ? 'Сохранение...' : editingPost ? 'Сохранить' : 'Создать'}
                </button>
                <button type="button" className={styles.cancelBtn} onClick={closeForm}>
                  Отмена
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Таблица постов */}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Тип</th>
                <th className={styles.th}>Контент</th>
                <th className={styles.th}>Статус</th>
                <th className={styles.th}>Публикация</th>
                <th className={styles.th}>Комментарии</th>
                <th className={styles.th}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={6} className={styles.loadingCell}>Загрузка...</td></tr>
              ) : posts.length === 0 ? (
                <tr><td colSpan={6} className={styles.emptyCell}>Постов пока нет</td></tr>
              ) : (
                posts.map((post) => {
                  const scheduled = isScheduled(post);
                  return (
                    <tr key={post.id} className={styles.row}>
                      <td className={styles.td}>
                        <span className={`${styles.typeBadge} ${styles[`typeBadge_${post.type}`]}`}>
                          {TYPE_LABELS[post.type]}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <span className={styles.postPreview}>
                          {post.content?.slice(0, 60) || post.event_title || post.link_title || post.link_url || '—'}
                          {(post.content?.length > 60) && '...'}
                        </span>
                      </td>
                      <td className={styles.td}>
                        {scheduled ? (
                          <span className={`${styles.statusBadge} ${styles.statusScheduled}`}>Отложен</span>
                        ) : post.published ? (
                          <span className={`${styles.statusBadge} ${styles.statusPublished}`}>Опубликован</span>
                        ) : (
                          <span className={`${styles.statusBadge} ${styles.statusDraft}`}>Черновик</span>
                        )}
                      </td>
                      <td className={styles.td}>{formatDateTime(post.published_at)}</td>
                      <td className={styles.td}>{post._count?.comments ?? 0}</td>
                      <td className={styles.td}>
                        <div className={styles.rowActions}>
                          <button
                            className={post.pinned ? styles.pinActiveBtn : styles.pinBtn}
                            onClick={() => handleTogglePin(post)}
                            title={post.pinned ? 'Открепить' : 'Закрепить'}
                          >
                            {post.pinned ? '📌 Закреплён' : '📌'}
                          </button>
                          <button className={styles.editBtn} onClick={() => openEdit(post)}>Изменить</button>
                          <button className={styles.deleteBtn} onClick={() => handleDelete(post.id)}>Удалить</button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminNews;
